import { useCallback, useEffect, useState } from 'react';

import { formatDate, formatMoney, toMoney } from '@betterment/core';
import { StepProps, useFlowState, useMatchMedia } from '@betterment/js-runtime';
import { Button, Icon } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import {
  Box,
  CenteredWorkflowLayout,
  Flex,
  Heading,
  Text,
  WorkflowHeader,
  theme,
  get as themeGet,
} from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import PortfolioTickerDetailsModal from './PortfolioTickerDetailsModal';

import { trackClick } from '../../features/track';
import { DynamicShimmerLoader, TextShimmerLoader } from '../../Shared/components/ShimmerLoaders';
import VR from '../../Shared/components/VR';
import { locationAssign } from '../../Shared/path-helpers';
import { urlBuilder } from '../../Shared/urlBuilder';
import { FundReallocation, PortfolioFlowState } from '../data';
import { useExternallyAdvisedPlanPortfolioQuery } from '../hooks';
import { convertToPercentDisplay, currentPortfolioName, currentPortfolioSubName } from '../models';

const Header = styled(Heading)`
  white-space: nowrap;
`;

const SelectCustomPortfolioButton = styled.button`
  background-color: transparent;
  border: solid 1px ${themeGet('colors.bmtGrey20')};
  border-radius: 3px;
  font-weight: normal;
  margin-top: 6px;
  padding: 1.6rem;
  text-align: left;
  width: 100%;

  &:hover {
    background-color: ${themeGet('colors.transparent')};
  }

  &:focus {
    outline: 2px solid ${themeGet('colors.bmtBlue')};
    outline-offset: 1px;
    text-decoration: none;
  }
`;

const ShimmerLoader = () => {
  return (
    <Box data-testid="loading-indicator">
      <Box>
        <TextShimmerLoader numLines={2} />
      </Box>
      <Box>
        <DynamicShimmerLoader height="184px" />
      </Box>
    </Box>
  );
};

const PortfolioSelectionStep: React.FC<React.PropsWithChildren<StepProps>> = ({ goToStep }) => {
  const {
    flowState: {
      values: { goalId, fundReallocation },
    },
    updateValues,
  } = useFlowState<PortfolioFlowState>();
  const { qdiaFund, isLoading, weightedSecurities, accountId } = useExternallyAdvisedPlanPortfolioQuery(goalId);
  const [hasPortfolioTickerDetailsModalDisplayed, setHasPortfolioTickerDetailsModalDisplayed] = useState(false);

  useEffect(() => {
    if (accountId) {
      updateValues({ accountId });
    }
  }, [accountId, updateValues]);

  const closePortfolioTickerDetailsModal = useCallback(() => {
    setHasPortfolioTickerDetailsModalDisplayed(false);
  }, []);

  const navigateToGoalSettings = useCallback(() => {
    locationAssign(urlBuilder.getGoalSettings.absolute({ goalId }));
  }, [goalId]);

  const portfolioIsCurrentlyQdia = () => {
    const qdiaSlug = qdiaFund?.slug;
    const qdiaAllocationPercent = weightedSecurities?.find(
      weightedSecurity => weightedSecurity.security?.slug === qdiaSlug
    )?.allocationPercent;
    return qdiaAllocationPercent === 100;
  };

  const chooseDefaultQdia = () => {
    if (portfolioIsCurrentlyQdia()) {
      goToStep('success');
    } else {
      const reallocation: FundReallocation = {
        fromName: currentPortfolioName(weightedSecurities!),
        fromSubname: currentPortfolioSubName(weightedSecurities!),
        toName: 'Target date fund',
        toSubname: qdiaFund?.name,
        fundAllocations: { [qdiaFund!.slug]: 100 },
      };
      goToStep('confirm', { fundReallocation: reallocation, previousStep: 'select' });
    }
  };

  const chooseCustomPortfolio = () => {
    const currentPortfolioNameVar = currentPortfolioName(weightedSecurities!);
    const reallocation: FundReallocation = {
      fromName: currentPortfolioNameVar,
      fromSubname: currentPortfolioSubName(weightedSecurities!),
      toName: 'Custom portfolio',
      toSubname: currentPortfolioNameVar === 'Custom portfolio' ? 'Allocations updated' : null,
    };
    goToStep('customize', { fundReallocation: fundReallocation?.fundAllocations ? fundReallocation : reallocation });
  };

  const isMobileVersion = !useMatchMedia('atLeastMedium');

  return (
    <CenteredWorkflowLayout variant="six-column">
      <CenteredWorkflowLayout.Slot name="header">
        <WorkflowHeader title="" backButtonHandler={navigateToGoalSettings} closeHandler={navigateToGoalSettings} />
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="content">
        <Box mb="8">
          <Header variant="primary" color="bmtGrey90" mb="0">
            How do you want to invest?
          </Header>
          <Text as="p" color="bmtGrey90">
            The default target date fund shown is based on your date of birth and a retirement age of 65.
          </Text>
        </Box>
        <Box borderRadius="4px" border="1px solid" borderColor="bmtGrey20" padding="6" mt="0">
          {isLoading && <ShimmerLoader />}
          {qdiaFund && (
            <>
              <Box>
                <Text mb="0" as="p" fontSize="2">
                  {qdiaFund.name}
                </Text>
                <SambaAutoLink
                  to={qdiaFund.prospectusUrl}
                  target="_blank"
                  style={{ fontSize: theme.fontSizes[1] }}
                  onClick={() => {
                    trackClick({ name: `SeeMore${qdiaFund.symbol || ''}`, flow: 'ExternallyAdvisedPlanFlow' });
                  }}
                >
                  {qdiaFund.symbol}
                </SambaAutoLink>
              </Box>
              <Box mb="6" borderRadius="4px">
                <Flex justifyContent="space-between" my="4" flexDirection={isMobileVersion ? 'column' : 'row'}>
                  <Box width="100%">
                    <Text as="p" color="bmtGrey50" mb="0" fontSize="0">
                      Historical performance
                    </Text>
                    {qdiaFund.historicalPerformances.map(item => (
                      <Flex key={item.timePeriod} justifyContent="space-between" alignItems="center">
                        <Flex.Item>
                          <Text as="p" color="bmtGrey90" fontSize="0">
                            {item.timePeriod}
                          </Text>
                        </Flex.Item>
                        <Flex.Item>
                          <Text as="p" color="bmtGrey90" fontSize="0">
                            {convertToPercentDisplay(item.percent)}
                          </Text>
                        </Flex.Item>
                      </Flex>
                    ))}
                  </Box>
                  {!isMobileVersion && <VR mx="8" />}
                  <Box width="100%">
                    <Text as="p" color="bmtGrey50" mb="0" mt={isMobileVersion ? '4' : '0'} fontSize="0">
                      General info
                    </Text>
                    <Flex justifyContent="space-between" alignItems="center">
                      <Flex.Item>
                        <Text as="p" color="bmtGrey90" fontSize="0">
                          Gross Exp ratio %
                        </Text>
                      </Flex.Item>
                      <Flex.Item>
                        <Text as="p" color="bmtGrey90" fontSize="0">
                          {convertToPercentDisplay(qdiaFund.expensePercent)}
                        </Text>
                      </Flex.Item>
                    </Flex>
                    <Flex justifyContent="space-between" alignItems="center">
                      <Flex.Item>
                        <Text as="p" color="bmtGrey90" fontSize="0">
                          Gross Exp ratio $ (per $1,000)
                        </Text>
                      </Flex.Item>
                      <Flex.Item>
                        <Text as="p" color="bmtGrey90" fontSize="0">
                          {formatMoney(toMoney(qdiaFund.expenseAmount.cents))}
                        </Text>
                      </Flex.Item>
                    </Flex>
                    <Flex justifyContent="space-between" alignItems="center">
                      <Flex.Item>
                        <Text as="p" color="bmtGrey90" fontSize="0">
                          Category
                        </Text>
                      </Flex.Item>
                      <Flex.Item>
                        <Text as="p" color="bmtGrey90" fontSize="0">
                          {qdiaFund.category}
                        </Text>
                      </Flex.Item>
                    </Flex>
                    <Flex justifyContent="space-between" alignItems="center">
                      <Flex.Item>
                        <Text as="p" color="bmtGrey90" fontSize="0">
                          Inception
                        </Text>
                      </Flex.Item>
                      <Flex.Item>
                        <Text as="p" color="bmtGrey90" fontSize="0">
                          {formatDate(qdiaFund.inceptionDate, 'longOrdinalSlashed')}
                        </Text>
                      </Flex.Item>
                    </Flex>
                  </Box>
                </Flex>
                <Box my="4">
                  <Button
                    variant="textPrimary"
                    onClick={() => {
                      trackClick({ name: `SeeMore${qdiaFund.slug}`, flow: 'ExternallyAdvisedPlanFlow' });
                      setHasPortfolioTickerDetailsModalDisplayed(true);
                    }}
                  >
                    See more
                  </Button>
                </Box>
              </Box>
              <Button
                variant="solidPrimary"
                css={{ mb: 'space.0', width: isMobileVersion ? '100%' : 'unset' }}
                onClick={() => {
                  chooseDefaultQdia();
                }}
              >
                Continue
              </Button>
            </>
          )}
        </Box>
        <br />
        {!isLoading && (
          <SelectCustomPortfolioButton onClick={() => chooseCustomPortfolio()}>
            <Flex justifyContent="space-between" alignItems="center">
              <Box p="0" className="has-text-right">
                <Text as="p" mb="0">
                  I want more flexibility
                </Text>
                <Text as="p" color="bmtGrey50" fontSize="1">
                  Build your custom portfolio from a full menu of supported funds
                </Text>
              </Box>
              <Box>
                <Icon name="CaretRight" size="small" color="icon.weak" />
              </Box>
            </Flex>
          </SelectCustomPortfolioButton>
        )}
        <Box my="4">
          {hasPortfolioTickerDetailsModalDisplayed && (
            <PortfolioTickerDetailsModal security={qdiaFund} closeHandler={closePortfolioTickerDetailsModal} />
          )}
        </Box>
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default PortfolioSelectionStep;
